<template>
  <div class="quick-response-vacancies">
    <CollapsibleSection
      :visible="open"
      :with-line="false"
      no-margin
      full-width
      @toggle="onToggle"
    >
      <template #toggle="{ toggle, isVisible }">
        <button
          class="quick-response-vacancies__toggle"
          @click="toggle"
        >
          <span class="quick-response-vacancies__title">
            <FireLabel text="new" />
            <span>Быстрый отклик</span>
          </span>
          <span class="collapsible-section__arrow">
            <ArrowIcon
              :direction="isVisible ? 'top' : 'bottom'"
              size="24"
              color="muted"
            />
          </span>
        </button>
      </template>
      <template #default>
        <div class="quick-response-vacancies__content">
          <p class="quick-response-vacancies__about">Это подборка подходящих вакансий. Один клик&nbsp;&mdash; и&nbsp;отклик
            отправится в&nbsp;компанию вместе с&nbsp;процентом соответствия вашего профиля требованиям позиции.</p>
          <div class="quick-response-vacancies__slider">
            <TransitionFade mode="out-in">
              <BasicSlider
                v-if="!finished"
                ref="slider"
                key="slider"
                :slides="slides"
                pagination-under
                prev-title="Предыдущая вакансия"
                next-title="Следующая вакансия"
                :show-pagination="false"
                :show-navigation="slides.length - handledCount > 1"
                :space-between="16"
                v-slot="{ slide: vacancy }"
              >
                <QuickVacancy
                  :key="`slide-${vacancy.id}`"
                  :vacancy="vacancy"
                  :responded="responded[vacancy.id]"
                  :banned="banned[vacancy.id]"
                  :loading="loadingActions[vacancy.id]"
                  @ban="ban"
                  @respond="respond"
                />
              </BasicSlider>
              <div
                v-else
                key="finish-screen"
                class="quick-response-vacancies__final-screen"
              >
                <NoContent
                  title="Вы посмотрели все вакансии"
                  description="Как только найдем новые подходящие вакансии, покажем их в этом блоке"
                  :icon="$options.finalScreenIcon"
                  :style="{ height: finishScreenHeight }"
                />
              </div>
            </TransitionFade>
          </div>
          <TransitionExpand>
            <div
              v-if="showFeedbackButton"
              class="quick-response-vacancies__review-link"
            >
              <BasicButton
                appearance="pseudo-link-thin"
                @click="showFeedbackModal = true"
              >
                Оценить функцию «Быстрый отклик»
              </BasicButton>
            </div>
          </TransitionExpand>
        </div>
      </template>
    </CollapsibleSection>
    <QuickVacanciesFeedbackModal
      :visible="showFeedbackModal"
      @close="onFeedbackModalClose"
    />
  </div>
</template>

<script>
  import { handleRequestError } from 'src/services/api.js';
  import { QuickResponsesService } from 'src/services/quick-responses.js';

  import BasicButton from 'src/components/BasicButton/BasicButton.vue';
  import BasicSlider from 'src/components/BasicSlider/BasicSlider.vue';
  import CollapsibleSection from 'src/components/CollapsibleSection/CollapsibleSection.vue';
  import FireLabel from 'src/components/FireLabel/FireLabel.vue';
  import ArrowIcon from 'src/components/icons/ArrowIcon/ArrowIcon.vue';
  import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
  import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';
  import TransitionFade from 'src/components/transitions/TransitionFade/TransitionFade.vue';
  import QuickVacanciesFeedbackModal
    from 'src/components/vacancies/QuickVacanciesFeedbackModal/QuickVacanciesFeedbackModal.vue';
  import QuickVacancy from 'src/components/vacancies/QuickVacancy/QuickVacancy.vue';

  import vacanciesAllReviewed from 'images/vacancies/vacancies.all_reviewed.svg';
  
  const finalScreenIcon = {
    src: vacanciesAllReviewed,
    width: '84px',
    height: '75px'
  };
  
  export default {
    name: 'QuickResponseVacancies',
    components: {
      TransitionExpand,
      QuickVacanciesFeedbackModal,
      NoContent,
      TransitionFade,
      ArrowIcon,
      QuickVacancy,
      BasicSlider,
      FireLabel,
      BasicButton,
      CollapsibleSection
    },
    finalScreenIcon,
    props: {
      vacancies: {
        type: Array,
        required: true
      },
      showFeedback: Boolean,
      isOpen: {
        type: Boolean,
        default: true
      },
    },
    emits: ['hide-feedback'],
    data() {
      /**
       *
       * @returns {{[key: number]: boolean}}
       */
      const vacancyFlags = () => Object.fromEntries(this.vacancies.map(v => [v.id, false]));

      return {
        open: this.isOpen,
        showFeedbackModal: false,
        showFeedbackButton: this.showFeedback,
        banned: vacancyFlags(),
        responded: vacancyFlags(),
        loadingActions: vacancyFlags(),
        slides: [...this.vacancies],
        handledCount: 0,
        // Чтобы избежать скачков при появлении конечного экрана
        finishScreenHeight: 'auto',
        currentSlideIndex: 0,
      };
    },
    watch: {
      finished(finished) {
        if (finished) {
          window.mixpanel.track('Rec vacancy block reached stub')
        }
      }
    },
    mounted() {
      if (!this.$refs.slider.swiperInstance) return;

      this.$refs.slider.swiperInstance.on('slideChange', (swiper) => {
        this.currentSlideIndex = swiper.activeIndex;
      });
    },
    methods: {
      onFeedbackModalClose(hasVoted) {
        this.showFeedbackModal = false;
        this.showFeedbackButton = !hasVoted;
        this.$emit('hide-feedback');
      },
      async onToggle(isVisible) {
        const toggleEvent = isVisible
          ? 'Rec vacancy block unroll'
          : 'Rec vacancy block roll up';
        window.mixpanel.track(toggleEvent);

        if (!isVisible) return;

        await this.$nextTick();

        if (!this.$refs.slider) return;

        this.slides = this.slides.filter(vacancy => !(this.banned[vacancy.id] || this.responded[vacancy.id]));

        if (this.$refs.slider.swiperInstance) {
          this.$refs.slider.swiperInstance.slideTo(this.currentSlideIndex, 0);
        }
      },
      ban(vacancy) {
        this.loadingActions[vacancy.id] = true;

        QuickResponsesService.ban(vacancy.id)
          .then(() => {
            this.banned[vacancy.id] = true;
            this.nextSlide('banned');
          })
          .catch(handleRequestError)
          .finally(() => {
            this.loadingActions[vacancy.id] = false;
          });
      },
      respond(vacancy) {
        this.loadingActions[vacancy.id] = true;
        QuickResponsesService.respond(vacancy.id)
          .then(() => {
            this.responded[vacancy.id] = true;
            this.nextSlide('responded');
          })
          .catch(handleRequestError)
          .finally(() => {
            this.loadingActions[vacancy.id] = false;
          });
      },
      /**
       * Делает красивый переход к следующему слайду.
       * Состоит из нескольких фаз:
       *  1. подготовка слайдера
       *  2. анимация текущего слайда
       *  3. фиксация нового состояния
       * @param {'responded'|'banned'} action
       */
      nextSlide(action) {
        const swiper = this.$refs.slider.swiperInstance;
        if (!swiper) return;
        // 1. Подготовка слайдера
        // Выключаем взаимодействия, чтобы юзер не сбил наш собственный переход
        swiper.disable();

        // Таймаут, потому что на самом слайде есть анимация и надо чтобы юзер её посмотрел
        setTimeout(() => {
          // Мы всегда листаем справа налево, но там может не быть DOM-элемента - в зацикленном слайдере он туда
          // подставляется в начале свайпа. Делаем жто здесь руками, чтобы следующий слайд не возникал из ниоткуда.
          swiper.loopFix({ direction: 'next' });

          // 2. Анимация текущего слайда. Суть анимации:
          // - анимируем ширину и margin слайда до 0px, чем сдвигаем на его место следующий слайд;
          // - сам QuickVacancy при этом фиксируем по ширине, чтобы его контент не изменялся;
          // - анимируем так же слайд вниз с затуханием и уменьшением, но scale на QuickVacancy - иначе слайд съезжает
          //   влево из-за уменьшения ширины (смещается transform origin).
          const slideEl = swiper.slides[swiper.activeIndex];
          const quickVacancyElement = slideEl.firstElementChild;
          quickVacancyElement.style.width = slideEl.style.width;

          const hiddenSlideClass = 'quick-response-vacancies__hidden-slide';
          slideEl.classList.add(
            hiddenSlideClass,
            `${hiddenSlideClass}--${action}`,
          );

          // Выключаем кнопки на следующем слайде, чтобы избежать мигания ховера
          const buttons = this.getNextSlideButtons(swiper);
          this.disableButtons(buttons);

          // 3. Фиксация нового состояния
          // Сам слайд не удаляем из `this.slides`, потому что это ломает swiper.
          slideEl.addEventListener('transitionend', (e) => {
            if (e.target !== e.currentTarget) return;

            this.handledCount += 1;
            if (this.finished) {
              this.finishScreenHeight = `${swiper.el.getBoundingClientRect().height}px`;
              return;
            }

            this.$nextTick()
              .then(() => {
                this.enableNextSlideButtons(buttons);
                this.removeActiveSlide(swiper)
                swiper.enable();
              });
          }, { once: true });
        }, 1500);
      },
      removeActiveSlide(swiper) {
        const { activeIndex } = swiper;

        // Слайды в swiper.slides могут менять порядок и не совпадать с текущим DOM,
        // swiper.activeIndex всегда указывает на текущий слайд
        swiper.slides[activeIndex].remove();
        // Обновляем swiper.slides после удаления слайда
        swiper.update();
        // После обновления слайдов сбиваются индексы (в частности realIndex),
        // из-за чего слайды могут схлопываться или показываться не те.
        // slideTo до текущего индекса их восстанавливает
        swiper.slideTo(swiper.activeIndex, 0, false);
        // Пересчитываем индексы слайдов в DOM, стоящих после удалённого.
        // Нужно для того, чтобы при ресайзе swiper мог получить правильный индекс слайда
        // и поставить слайдеру правильную высоту.
        // Если этого не делать, то будет выбран удалённый слайд, у которого индекс NaN и высота 0.
        swiper.slides.forEach(slide => {
          const index = +slide.getAttribute('data-swiper-slide-index');

          if (swiper.activeIndex < index) {
            slide.setAttribute('data-swiper-slide-index', index - 1);
          }
        });
      },
      // eslint-disable-next-line consistent-return
      getNextSlideButtons(swiper) {
        const nextSlide = swiper.slides[(swiper.activeIndex + 1) % swiper.slides.length];
        if (nextSlide) {
          return nextSlide.querySelectorAll('.quick-vacancy__controls button');
        }
      },
      disableButtons(buttons) {
        if (buttons) {
          buttons.forEach(button => {
            // eslint-disable-next-line no-param-reassign
            button.style.pointerEvents = 'none';
          });
        }
      },
      enableNextSlideButtons(buttons) {
        if (buttons) {
          buttons.forEach(button => {
            // eslint-disable-next-line no-param-reassign
            button.style.pointerEvents = 'initial';
          });
        }
      },
    },
    computed: {
      finished() {
        return this.handledCount === this.vacancies.length;
      },
    }
  };
</script>
